import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatDate, today } from '../../utils/helpers';
import { computeProfitAndLoss } from '../../utils/financials';
import { downloadElementAsPdf } from '../../utils/exportPdf';
import { FileDown } from 'lucide-react';

// First day of the current Indian financial year (1 April).
const fyStart = () => {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-04-01`;
};

const Amt = ({ value, bold }) => (
  <span className={bold ? 'font-bold' : ''} style={{ fontVariantNumeric: 'tabular-nums' }}>
    {formatCurrency(value)}
  </span>
);

export default function ProfitAndLoss() {
  const { state } = useApp();
  const [startDate, setStartDate] = useState(fyStart());
  const [endDate, setEndDate] = useState(today());
  const [busy, setBusy] = useState(false);

  const pl = useMemo(() => computeProfitAndLoss(state, startDate, endDate), [state, startDate, endDate]);
  const business = state.company?.name || 'MayaSoft ERP';

  const download = async () => {
    if (busy) return;
    setBusy(true);
    try { await downloadElementAsPdf(document.getElementById('pl-print-area'), `Profit-and-Loss-${endDate}`); }
    catch (e) { console.error('P&L PDF failed:', e); }
    finally { setBusy(false); }
  };

  const Section = ({ title, rows, total, totalLabel, positive }) => (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
      <div className="divide-y divide-gray-100">
        {rows.length === 0 && <div className="py-2 text-sm text-gray-400">No entries in this period</div>}
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between py-1.5 text-sm">
            <span className="text-gray-700">{r.label}</span>
            <Amt value={r.amount} />
          </div>
        ))}
      </div>
      {total != null && (
        <div className={`flex justify-between py-2 mt-1 border-t-2 border-gray-300 text-sm font-bold ${positive ? 'text-green-700' : 'text-gray-900'}`}>
          <span>{totalLabel}</span>
          <Amt value={total} bold />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Profit &amp; Loss</h1>
        <button onClick={download} disabled={busy} className="btn btn-secondary flex items-center gap-2 disabled:opacity-50">
          <FileDown size={16} /> {busy ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div id="pl-print-area" style={{ background: '#fff' }}>
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold text-gray-900">{business}</h2>
            <p className="text-sm text-gray-600">Profit &amp; Loss Statement</p>
            <p className="text-xs text-gray-500">For the period {formatDate(startDate)} to {formatDate(endDate)}</p>
          </div>

          <Section title="Income (Ledger accounts)" rows={pl.incomeRows} total={pl.totalIncome} totalLabel="Total Income" />
          <Section title="Direct Costs / Purchases" rows={pl.directRows} total={pl.totalDirect} totalLabel="Total Direct Costs" />

          <div className="flex justify-between py-2 mb-5 bg-blue-50 px-3 rounded text-sm font-bold text-blue-800">
            <span>Gross Profit (Income − Direct Costs)</span>
            <Amt value={pl.grossProfit} bold />
          </div>

          <Section title="Indirect / Operating Expenses" rows={pl.indirectRows} total={pl.totalIndirect} totalLabel="Total Expenses" />

          <div className={`flex justify-between items-center py-3 px-3 rounded text-base font-bold ${pl.netProfit >= 0 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            <span>{pl.netProfit >= 0 ? 'Net Profit' : 'Net Loss'}</span>
            <Amt value={pl.netProfit} bold />
          </div>

          <p className="text-[10px] text-gray-400 mt-4 text-center">
            Built from the linked ledger accounts; figures are GST-exclusive (tax posts to its own ledger). Generated on {formatDate(today())}.
          </p>
        </div>
      </div>
    </div>
  );
}
